import React, { useEffect, useRef, useState } from "react";
import FaceSheetPrintView from "./face_sheet_workspace/print_view";
import FaceSheetModalForm from "./face_sheet_workspace/modal_form";
import {
  AdditionalResultsSection,
  DiagnosesSection,
  ImagingSection,
  LabsSection,
  MedicationsSection,
  NotesSection,
  OverviewSection,
} from "./face_sheet_workspace/sections";
import type { FaceSheetSectionKey, FaceSheetWorkspaceProps } from "./face_sheet_workspace/types";

const UNSAVED_CHANGES_MESSAGE = "You have unsaved face sheet changes. Leave without saving?";

function NoticeActionForm({
  action,
  authenticityToken,
  buttonLabel,
  confirmMessage,
  fields = [],
}: {
  action: string;
  authenticityToken: string;
  buttonLabel: string;
  confirmMessage?: string;
  fields?: Array<{ name: string; value: string }>;
}) {
  return (
    <form method="post" action={action}>
      <input type="hidden" name="authenticity_token" value={authenticityToken} />
      {fields.map((field) => (
        <input key={field.name} type="hidden" name={field.name} value={field.value} />
      ))}
      <button type="submit" className="app-button-secondary" data-turbo-confirm={confirmMessage}>
        {buttonLabel}
      </button>
    </form>
  );
}

function FaceSheetWarnings({
  authenticityToken,
  warnings,
}: Pick<FaceSheetWorkspaceProps, "authenticityToken" | "warnings">) {
  if (!warnings?.missingLabExtraction) return null;

  return (
    <div className="space-y-3">
      {warnings.missingLabExtraction ? (
        <section className="rounded-lg border border-sky-200 bg-sky-50/80 p-4 dark:border-sky-900/60 dark:bg-sky-950/20">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700 dark:text-sky-300">Missing extraction</p>
              <p className="mt-1 text-sm leading-6 text-sky-950 dark:text-sky-100">{warnings.missingLabExtraction.message}</p>
            </div>
            <NoticeActionForm
              action={warnings.missingLabExtraction.extractAllUrl}
              authenticityToken={authenticityToken}
              buttonLabel="Queue extraction"
              fields={[{ name: "origin", value: "face_sheet" }]}
            />
          </div>

          {warnings.missingLabExtraction.documents.length > 0 ? (
            <ul className="mt-3 space-y-1.5 text-sm text-sky-950 dark:text-sky-100">
              {warnings.missingLabExtraction.documents.map((document) => (
                <li key={document.id}>
                  {document.openUrl ? (
                    <a href={document.openUrl} className="font-medium text-sky-700 hover:text-sky-800 hover:underline dark:text-sky-300 dark:hover:text-sky-200">
                      {document.title}
                    </a>
                  ) : (
                    document.title
                  )}
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

export default function FaceSheetWorkspace({
  authenticityToken,
  paths,
  originalFacesheetUrl,
  payload = {},
  summary = {},
  warnings,
}: FaceSheetWorkspaceProps) {
  const [openSection, setOpenSection] = useState<FaceSheetSectionKey | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const navigationConfirmedRef = useRef(false);
  const patientSnapshot = payload.patient_snapshot || {};
  const careContext = payload.care_context || {};
  const medications = payload.medications || [];
  const extractedMedications = payload.extracted_medications || [];
  const extractedAllergies = payload.extracted_allergies || [];
  const labs = payload.labs || [];
  const operationSummary = payload.operation_summary;
  const additionalResults = payload.additional_results || {};
  const imaging = payload.imaging || [];
  const extractedImagingResults = payload.extracted_imaging_results || [];
  const diagnoses = payload.diagnoses || [];

  useEffect(() => {
    if (!openSection) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;

      event.preventDefault();

      if (hasUnsavedChanges && !window.confirm(UNSAVED_CHANGES_MESSAGE)) return;

      setHasUnsavedChanges(false);
      setOpenSection(null);
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [hasUnsavedChanges, openSection]);

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (navigationConfirmedRef.current) return;

      event.preventDefault();
      event.returnValue = "";
    };

    const handleBeforeVisit = (event: Event) => {
      if (navigationConfirmedRef.current) return;

      const nextUrl = (event as Event & { detail?: { url?: string } }).detail?.url;
      if (!nextUrl) return;

      event.preventDefault();
      if (!window.confirm(UNSAVED_CHANGES_MESSAGE)) return;

      navigationConfirmedRef.current = true;
      setHasUnsavedChanges(false);
      window.location.assign(nextUrl);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("turbo:before-visit", handleBeforeVisit);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("turbo:before-visit", handleBeforeVisit);
    };
  }, [hasUnsavedChanges]);

  const requestCloseSection = () => {
    if (hasUnsavedChanges && !window.confirm(UNSAVED_CHANGES_MESSAGE)) return;

    setHasUnsavedChanges(false);
    setOpenSection(null);
  };

  const handleDirtyChange = (dirty: boolean) => {
    if (dirty) navigationConfirmedRef.current = false;
    setHasUnsavedChanges(dirty);
  };

  const handleSave = () => {
    navigationConfirmedRef.current = true;
    setHasUnsavedChanges(false);
  };

  return (
    <div>
      <div className="face-sheet-screen space-y-4">
        <section className="face-sheet-print-surface rounded-lg border border-neutral-200 bg-white p-3 dark:border-neutral-800 dark:bg-neutral-950 md:p-4">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div>
              <p className="app-kicker">Consult prep</p>
              <h2 className="mt-0.5 text-xl font-semibold text-neutral-950 dark:text-white">Face sheet</h2>
              <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
                Generated {summary.generatedAtLabel || "just now"}
                {summary.lastUpdatedAtLabel ? ` • Last updated ${summary.lastUpdatedAtLabel}` : ""}
              </p>
            </div>

            <div className="flex flex-wrap gap-1.5">
              <button type="button" className="app-button-secondary" onClick={() => window.print()}>
                Print
              </button>
            </div>
          </div>

        </section>

        <FaceSheetWarnings authenticityToken={authenticityToken} warnings={warnings} />

        <OverviewSection
          patientSnapshot={patientSnapshot}
          careContext={careContext}
          editable
          onEdit={() => setOpenSection("demographics")}
        />
        <MedicationsSection
          medications={medications}
          extractedMedications={extractedMedications}
          extractedAllergies={extractedAllergies}
          intakeSubmissionTemplate={paths.intakeSubmissionTemplate}
          compareDocumentTemplate={paths.compareDocumentTemplate}
          documentTemplate={paths.documentTemplate}
          editable
          onEdit={() => setOpenSection("medications")}
        />
        <LabsSection
          labs={labs}
          operationSummary={operationSummary}
          compareDocumentTemplate={paths.compareDocumentTemplate}
          documentTemplate={paths.documentTemplate}
          originalFacesheetUrl={originalFacesheetUrl}
          editable
          onEdit={() => setOpenSection("labs")}
        />
        <AdditionalResultsSection
          additionalResults={additionalResults}
          compareDocumentTemplate={paths.compareDocumentTemplate}
          documentTemplate={paths.documentTemplate}
          editable
          onEdit={() => setOpenSection("additional_results")}
        />
        <ImagingSection
          imaging={imaging}
          extractedImagingResults={extractedImagingResults}
          compareDocumentTemplate={paths.compareDocumentTemplate}
          documentTemplate={paths.documentTemplate}
          editable
          onEdit={() => setOpenSection("imaging")}
        />
        <DiagnosesSection
          diagnoses={diagnoses}
          editable
          onEdit={() => setOpenSection("diagnoses")}
        />
        <NotesSection
          notes={payload.staff_notes}
          editable
          onEdit={() => setOpenSection("notes")}
        />
      </div>

      <FaceSheetPrintView payload={payload} />

      <FaceSheetModalForm
        openSection={openSection}
        payload={payload}
        paths={paths}
        authenticityToken={authenticityToken}
        onClose={requestCloseSection}
        onDirtyChange={handleDirtyChange}
        onSubmit={handleSave}
      />
    </div>
  );
}
